import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useColors } from '@/contexts/ThemeContext';
import { spring } from '@/lib/motion';
import { Spinner } from './Spinner';

export type ButtonSize = 'large' | 'base' | 'small' | 'sm' | 'icon-small' | 'icon-base' | 'icon-large';

export type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'ghost' | 'destructive' | 'ghost-destructive';

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;
type MotionOnlyProps = Omit<HTMLMotionProps<'button'>, keyof NativeButtonProps>;

export interface ButtonProps extends NativeButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  showIndicator?: boolean;
  children?: ReactNode;
  motionProps?: MotionOnlyProps;
}

const sizeStyles: Record<ButtonSize, string> = {
  large: 'min-h-12 px-4 py-3 text-[13px]',
  base: 'min-h-10 px-3 py-2 text-[13px]',
  small: 'min-h-8 px-2 py-1.5 text-[13px]',
  sm: 'min-h-8 px-2 py-1.5 text-[13px]',
  'icon-small': 'h-8 w-8 p-1.5',
  'icon-base': 'h-10 w-10 p-2',
  'icon-large': 'h-12 w-12 p-3',
};

const iconSizes: Record<ButtonSize, string> = {
  large: 'h-4 w-4',
  base: 'h-4 w-4',
  small: 'h-3.5 w-3.5',
  sm: 'h-3.5 w-3.5',
  'icon-small': 'h-4 w-4',
  'icon-base': 'h-5 w-5',
  'icon-large': 'h-6 w-6',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'base',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      showIndicator = false,
      disabled,
      className,
      children,
      motionProps,
      ...props
    },
    ref
  ) => {
    const colors = useColors();
    const prefersReducedMotion = useReducedMotion();
    const isIconOnly = size.startsWith('icon-');
    const isDisabled = disabled || isLoading;

    const variantColors = getVariantColors(variant, colors, isDisabled);
    const showShimmer = isLoading && (variant === 'primary' || variant === 'destructive');

    const pressAnim = prefersReducedMotion || isDisabled
      ? undefined
      : { whileTap: { scale: 0.97 }, transition: spring.snappy };

    return (
      <motion.button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          'relative inline-flex items-center justify-center gap-2 overflow-hidden',
          'rounded-lg font-bold cursor-pointer select-none',
          'transition-[background-color,color,border-color,box-shadow] duration-200 ease-[var(--ease-standard)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-ring)]',
          'disabled:cursor-not-allowed',
          !isDisabled && 'hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-4px_rgba(0,0,0,0.12)]',
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        style={{
          backgroundColor: variantColors.background,
          color: variantColors.foreground,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: variantColors.border,
          fontFamily: 'var(--font-system, "Area", sans-serif)',
          fontWeight: 700,
          letterSpacing: '0',
        }}
        {...pressAnim}
        {...motionProps}
        {...(props as HTMLMotionProps<'button'>)}
      >
        {showIndicator && (
          <span className="absolute -left-1 -top-1 flex h-3 w-3" aria-label="indicator">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-success" />
          </span>
        )}

        {isLoading && <Spinner size="sm" className={cn(iconSizes[size])} />}

        {!isLoading && leftIcon && (
          <span className={cn('shrink-0', iconSizes[size])}>{leftIcon}</span>
        )}

        {/* Children wrapper — inline-flex para que cuando se pase un icono
            + texto como children (en lugar de via leftIcon/rightIcon prop)
            se layouteen en una sola fila. min-w-0 permite truncar. */}
        {!isIconOnly && children && (
          <span className="inline-flex items-center gap-1.5 min-w-0 truncate">
            {children}
          </span>
        )}

        {isIconOnly && children && (
          <span className={cn('shrink-0', iconSizes[size])}>{children}</span>
        )}

        {!isLoading && rightIcon && (
          <span className={cn('shrink-0', iconSizes[size])}>{rightIcon}</span>
        )}

        {showShimmer && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.4s linear infinite',
            }}
          />
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

function getVariantColors(
  variant: ButtonVariant,
  colors: ReturnType<typeof useColors>,
  isDisabled: boolean
) {
  const opacity = isDisabled ? 0.5 : 1;

  const variants: Record<ButtonVariant, { background: string; foreground: string; border: string }> = {
    primary: {
      background: colors.brand.primary,
      foreground: colors.brand.onPrimary,
      border: colors.brand.primary,
    },
    secondary: {
      background: colors.interactive.background,
      foreground: colors.foreground.primary,
      border: colors.interactive.background,
    },
    outlined: {
      background: colors.background.primary,
      foreground: colors.foreground.primary,
      border: colors.border.primary,
    },
    ghost: {
      background: 'transparent',
      foreground: colors.foreground.primary,
      border: 'transparent',
    },
    destructive: {
      background: colors.status.error,
      foreground: '#FFFFFF',
      border: colors.status.error,
    },
    'ghost-destructive': {
      background: 'transparent',
      foreground: colors.status.error,
      border: 'transparent',
    },
  };

  const variantColors = variants[variant];

  if (isDisabled && opacity < 1) {
    return {
      background: addOpacity(variantColors.background, opacity),
      foreground: addOpacity(variantColors.foreground, opacity),
      border: addOpacity(variantColors.border, opacity),
    };
  }

  return variantColors;
}

function addOpacity(color: string, opacity: number): string {
  if (color.startsWith('rgba')) {
    return color.replace(/[\d.]+\)$/, `${opacity})`);
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  return color;
}

export { Button };
export default Button;
